// React
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Redux
import { useDispatch, useSelector } from "react-redux";

// Components
import { Select, translateMessage } from "@delibrandev/utils";
import { LuSave, LuTrash2 } from "react-icons/lu";

import {
  AdminInput as Input,
  AdminTextarea as Textarea,
  SearchMetadata,
  slugify,
} from "../../../../../components/adminForm";

import { Alergens, emptyAllergenSelection } from "./alergens";

// API
import {
  createProduct,
  updateProduct,
  updateProductStatus,
  deleteProductsBatch,
  getProduct,
} from "../../../../../api/products";
import { EditorDialog } from "./editorDialog";
import { callChatGTP, IconIA } from "../../../../../api/ia";

// Reducers
import {
  setAlert,
  setLoading,
  updateFloatPanel,
  updateFloatPanelData,
  requestTableRefresh,
} from "../../../../../reducers/userConfigSlice";

export const Details = ({
  referenceLayout = false,
  actionsTarget = null,
  organization = null,
  onBusyChange = () => {},
  saveSections = async () => {},
  onSaved = () => {},
  onDirty = () => {},
}) => {
  const dispatch = useDispatch();

  const isEmpty = useSelector(
    (state) => state.aplicationConfig.userConfig.floatPanel.isEmpty,
  );

  const data = useSelector(
    (state) => state.aplicationConfig.userConfig.floatPanel.data,
  );

  const queryId = useSelector(
    (state) => state.aplicationConfig.userConfig.floatPanel.queryId,
  );

  const isCreating = isEmpty;
  const [draftAllergens, setDraftAllergens] = useState(() => {
    let value = data?.allergens;
    for (let i = 0; i < 2 && typeof value === "string"; i++) {
      try {
        value = JSON.parse(value);
      } catch {
        value = {};
      }
    }
    return { ...emptyAllergenSelection, ...(value?.allergens || value || {}) };
  });
  const [savedVersion, setSavedVersion] = useState(0);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [hasLote, setHasLote] = useState(data?.hasBatch ?? false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [removedImages, setRemovedImages] = useState([]);
  const inFlight = useRef(false);
  const [available, setAvailable] = useState(data?.status !== "INACTIVE");
  const [publication, setPublication] = useState(data?.status || "DRAFT");
  useEffect(() => {
    onBusyChange(saving);
  }, [saving, onBusyChange]);

  const handleSwitchChange = () => {
    setHasLote((currentValue) => !currentValue);
  };

  const getMeasurementUnit = () => {
    const unidadesSelect = getElement("unidadesSelect");

    if (!unidadesSelect) {
      return null;
    }

    /*
     * Se mantiene innerHTML porque el componente Select de
     * @delibrandev/utils puede no comportarse como un <select> nativo.
     */
    const selectedValue =
      unidadesSelect.value || unidadesSelect.innerHTML || "";

    if (selectedValue.includes("Unidad")) {
      return "unit";
    }

    if (selectedValue.includes("Kg")) {
      return "kg";
    }

    if (selectedValue.includes("L")) {
      return "l";
    }

    return null;
  };

  const renameImage = (file, position) => {
    const newFileName = `${position}-${file.name}`;

    return new File([file], newFileName, {
      type: file.type,
    });
  };

  const getFiles = () => {
    return Array.from({ length: 5 })
      .map((_, index) => {
        const inputElement = getElement(`img-alt-${index}`);
        const file = inputElement?.files?.[0];

        if (file) {
          return renameImage(file, index);
        }

        return undefined;
      })
      .filter((file) => file !== undefined);
  };

  const handleData = async (statusOverride) => {
    if (inFlight.current) return;
    const nameValue = getElementValue("nameInput").trim();
    const slugValue = getElementValue("seoUrlInput").trim();
    const errors = {};
    if (!nameValue) errors.name = "Escribe el nombre del producto.";
    if (slugValue && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slugValue))
      errors.slug = "Usa letras minúsculas, números y guiones entre palabras.";
    const gross = getElementValue("pvimpinclInput");
    if (
      (statusOverride === "PUBLISHED" && gross === "") ||
      (gross !== "" && (!Number.isFinite(Number(gross)) || Number(gross) < 0))
    )
      errors.price = "Escribe un precio de venta válido (cero o superior).";
    setFieldErrors(errors);
    if (Object.keys(errors).length) {
      getElement(
        errors.name
          ? "nameInput"
          : errors.price
            ? "pvimpinclInput"
            : "seoUrlInput",
      )?.focus();
      return;
    }
    inFlight.current = true;
    setSaving(true);
    setSaveError("");
    dispatch(setLoading(true));

    try {
      // SwitchText inverts its native checked value; the editor state is authoritative.
      const hasBatch = hasLote;

      /*
       * En creación data puede ser null.
       *
       * Al crear el producto parte como DRAFT.
       * Al editar conserva el estado actual.
       */
      const status =
        typeof statusOverride === "string"
          ? statusOverride === "PUBLISHED" && !available
            ? "INACTIVE"
            : statusOverride
          : publication;

      const name = getElementValue("nameInput");
      const urlSlug = getElementValue("seoUrlInput").trim() || slugify(name);

      const payload = {
        reference: getElementValue("referenceInput") || null,
        barcode: getElementValue("barcodeInput") || null,
        name: name || null,
        shortName: getElementValue("nameTPVInput") || null,
        description: getElementValue("descriptionProduct") || null,
        price: getElementValue("pvimpexclInput") || null,
        order: getElementValue("order") || null,

        tax: {
          value: parseInt(getElementValue("taxSelect"), 10) || 0,
          type: "percentage",
          name: "IVA",
        },

        comparisonPrice: getElementValue("pvcomparative") || null,
        status: referenceLayout
          ? isCreating
            ? "DRAFT"
            : data?.status || "DRAFT"
          : status,
        allergens: draftAllergens,

        hasBatch,

        batchNumber: hasBatch ? getElementValue("loteInput") || null : null,

        measurementUnit: getMeasurementUnit(),

        seo: {
          title: getElementValue("seoNameInput") || name,

          description: getElementValue("seoDescriptionInput") || name,

          keywords: urlSlug,
        },

        imagesToDelete: JSON.stringify(
          (data?.images || [])
            .filter((url) => {
              const position = Number(url.split("/").pop()?.split("-")[0]);
              return (
                removedImages.includes(position) ||
                Boolean(getElement(`img-alt-${position}`)?.files?.[0])
              );
            })
            .map((url) => url.split("/").pop()),
        ),
        defaultImageIndex: 0,
        files: getFiles(),

        urlSlug,
      };

      const response = isCreating
        ? await createProduct(payload)
        : await updateProduct(queryId, payload);

      if (response?.error === false) {
        const savedId = isCreating ? response.data?.productId : queryId;
        if (isCreating) {
          const product = response.data;
          if (!product?.productId) {
            setSaveError(
              "El servidor no devolvió el identificador del producto. Actualiza el listado antes de continuar.",
            );
            return;
          }
          dispatch(
            updateFloatPanel({
              visible: true,
              queryId: product.productId,
              isEmpty: false,
              data: product,
            }),
          );
        }
        if (referenceLayout) {
          try {
            await saveSections(savedId);
          } catch (sectionError) {
            throw Error(
              `Los datos principales ya están guardados. ${sectionError.message || "Falta guardar alguna sección; vuelve a intentarlo."}`,
            );
          }
          const published = await updateProductStatus(savedId, status);
          if (published?.error !== false || published.data !== status)
            throw Error(
              "Los datos se guardaron, pero no se pudo cambiar el estado. Vuelve a guardar.",
            );
        }
        if (referenceLayout) {
          const refreshed = await getProduct(savedId);
          if (refreshed?.error === false)
            dispatch(updateFloatPanelData({ data: refreshed.data }));
          else
            dispatch(updateFloatPanelData({ data: { ...data, name, status } }));
        } else if (!isCreating)
          dispatch(updateFloatPanelData({ data: { ...data, name, status } }));
        setRemovedImages([]);
        setSavedVersion((v) => v + 1);
        setPublication(status);
        onSaved();
        dispatch(requestTableRefresh());
        dispatch(
          setAlert(
            translateMessage(response?.message || "Operación realizada"),
          ),
        );

        return;
      }

      if (
        response?.message === "urlSlug already in use for this corporation" ||
        /Products_corporationId_urlSlug_unique/.test(response?.message || "")
      ) {
        setFieldErrors({
          slug: "Este enlace ya lo usa otro producto de tu negocio. Escribe uno diferente.",
        });
        setSaveError("Cambia el enlace del producto para poder guardarlo.");
        return;
      }
      setSaveError(
        "No se pudo guardar el producto. Revisa los datos y vuelve a intentarlo.",
      );
      dispatch(setAlert(translateMessage(response?.message || "Bad request")));

      console.error("Error en la operación:", response);
    } catch (error) {
      setSaveError(
        error?.message ||
          "No se pudo guardar el producto. Tus datos se conservan; vuelve a intentarlo.",
      );
      console.error("Error guardando el producto:", error);

      dispatch(
        setAlert(
          translateMessage(
            error?.response?.data?.message || error?.message || "Bad request",
          ),
        ),
      );
    } finally {
      inFlight.current = false;
      setSaving(false);
      dispatch(setLoading(false));
    }
  };

  const actions = (
    <div className="productEditorActions" aria-busy={saving}>
      {!isCreating && (
        <button
          type="button"
          className="referenceDelete"
          disabled={saving}
          onClick={() => setDeleteOpen(true)}
        >
          <LuTrash2 />
          Eliminar
        </button>
      )}
      <button
        type="button"
        className="customButton"
        disabled={saving}
        onClick={() => handleData("DRAFT")}
      >
        {saving ? "Guardando…" : "Guardar como borrador"}
      </button>
      <button
        type="button"
        className="customButton customButtonColor2"
        disabled={saving}
        onClick={() => handleData("PUBLISHED")}
      >
        <LuSave aria-hidden="true" />
        {saving ? "Guardando…" : "Publicar"}
      </button>
    </div>
  );
  return (
    <div
      className={referenceLayout ? "productReferenceDetails" : "productDetails"}
      aria-busy={saving}
      data-editor-write-pending={saving}
    >
      {actionsTarget && createPortal(actions, actionsTarget)}
      {deleteOpen && (
        <EditorDialog
          title="¿Eliminar producto?"
          description="El producto se retirará del catálogo. Esta acción no se puede deshacer desde esta ficha."
          busy={saving}
          onClose={() => setDeleteOpen(false)}
          footer={
            <>
              <button
                type="button"
                className="referenceOutline"
                disabled={saving}
                onClick={() => setDeleteOpen(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="referenceDanger"
                disabled={saving}
                onClick={async () => {
                  if (inFlight.current) return;
                  inFlight.current = true;
                  setSaving(true);
                  try {
                    const result = await deleteProductsBatch([queryId]);
                    if (result?.error !== false) throw Error();
                    dispatch(requestTableRefresh());
                    dispatch(
                      updateFloatPanel({
                        visible: false,
                        queryId: null,
                        isEmpty: true,
                      }),
                    );
                  } catch {
                    setSaveError(
                      "No se pudo eliminar el producto. Vuelve a intentarlo.",
                    );
                    setDeleteOpen(false);
                  } finally {
                    inFlight.current = false;
                    setSaving(false);
                  }
                }}
              >
                {saving ? "Eliminando…" : "Eliminar producto"}
              </button>
            </>
          }
        />
      )}
      {saveError && <p role="alert">{saveError}</p>}
      <section className="productFormCard">
        {referenceLayout && (
          <header>
            <h2>Detalles</h2>
            <p>
              Los datos con los que se identifica el producto en la tienda, el
              TPV y el almacén.
            </p>
          </header>
        )}
        {!referenceLayout && (
          <p className="formIntroduction">
            {isCreating
              ? "Empieza por el nombre. Puedes guardar un borrador y completar el resto después."
              : "Revisa los datos. Los cambios se aplicarán a este producto al guardar."}
          </p>
        )}
        <Detail
          referenceLayout={referenceLayout}
          savedVersion={savedVersion}
          onDraftChange={onDirty}
          available={available}
          onAvailableChange={setAvailable}
          onNameChange={() =>
            setFieldErrors((previous) => ({ ...previous, name: null }))
          }
          fieldErrors={fieldErrors}
          data={data}
          hasLote={hasLote}
          handleSwitchChange={handleSwitchChange}
          handleData={() => handleData()}
          saving={saving}
          removedImages={removedImages}
          onRemoveImage={(position) => {
            onDirty();
            setRemovedImages((previous) =>
              previous.includes(position)
                ? previous.filter((value) => value !== position)
                : [...previous, position],
            );
          }}
        />
      </section>
      <section className="productFormCard productPriceSection">
        <header>
          <h2>Precio</h2>
          <p>
            El precio con IVA es el que se aplica al producto en tienda y TPV.
          </p>
        </header>
        <div className="productCardFields">
          <Price data={data} error={fieldErrors.price} />
        </div>
      </section>
      {isCreating && !referenceLayout && (
        <section className="productFormCard productAllergens">
          <header>
            <h2>Alérgenos</h2>
            <p>
              Selecciona los alérgenos del producto. Se guardarán al crear el
              borrador o publicarlo.
            </p>
          </header>
          <div className="productCardFields">
            <Alergens
              draftValue={draftAllergens}
              onDraftChange={setDraftAllergens}
              disabled={saving}
            />
          </div>
        </section>
      )}
      {typeof organization === "function"
        ? organization({ draftAllergens, setDraftAllergens, saving })
        : organization}
      <section className="productFormCard productSeoSection">
        {referenceLayout && (
          <header>
            <h2>SEO</h2>
            <p>Cómo aparece el producto en los buscadores.</p>
          </header>
        )}
        <SearchMetadata
          expanded={referenceLayout}
          data={data}
          nameId="nameInput"
          slugError={fieldErrors.slug}
          onSlugChange={() =>
            setFieldErrors((previous) => ({ ...previous, slug: null }))
          }
        />
      </section>
    </div>
  );
};

// Stable component identities preserve uncontrolled draft fields on batch toggles.
const getElement = (id) => document.getElementById(id);
const getElementValue = (id) => getElement(id)?.value ?? "";

const Price = ({ data, error }) => {
  const price = Number(data?.price);

  // Older multipart responses can contain a JSON-encoded tax object.
  let storedTax = data?.tax;
  if (typeof storedTax === "string") {
    try {
      storedTax = JSON.parse(storedTax);
    } catch {
      /* Fall back to the default rate below. */
    }
  }
  const taxData = typeof storedTax === "object" ? storedTax?.value : storedTax;

  const tax = Number.isFinite(Number(taxData)) ? Number(taxData) : 21;
  const taxRates = [0, 4, 10, 21];
  if (!taxRates.includes(tax)) taxRates.push(tax);
  const syncPrice = (
    value,
    includesTax = false,
    rate = Number(getElementValue("taxSelect")),
  ) => {
    const target = getElement(
      includesTax ? "pvimpexclInput" : "pvimpinclInput",
    );
    if (!target) return;
    const amount = Number(value);
    target.value =
      value === "" || !Number.isFinite(amount)
        ? ""
        : (includesTax
            ? amount / (1 + rate / 100)
            : amount * (1 + rate / 100)
          ).toFixed(includesTax ? 6 : 2);
  };

  const priceWithTax = Number.isFinite(price)
    ? (price + (price * tax) / 100).toFixed(2)
    : "";

  return (
    <>
      <div className="row mb-3 mt-3">
        <div className="col-md-6 col-sm-12">
          <label className="visually-hidden" htmlFor="unidadesSelect">
            Unidad de medida
          </label>
          <Select
            id="unidadesSelect"
            values={[
              {
                id: "Unidad",
                name: "Unidad",
              },
              {
                id: "Kg",
                name: "Kg",
              },
              {
                id: "L",
                name: "L",
              },
            ]}
            label="Unidad de medida"
            description="Unidades para medir el artículo"
            classname=""
            value={data?.measurementUnit ?? data?.measureUnit ?? 0}
          />
        </div>

        <div className="col-md-6 col-sm-12">
          <label className="visually-hidden" htmlFor="taxSelect">
            IVA
          </label>
          <Select
            id="taxSelect"
            values={taxRates.map((rate) => ({ id: rate, name: `${rate}%` }))}
            callback={(rate) =>
              syncPrice(getElementValue("pvimpexclInput"), false, Number(rate))
            }
            label="IVA"
            description="Iva a aplicar"
            classname=""
            value={tax}
          />
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-md-4 col-sm-12">
          <Input
            id="pvimpexclInput"
            type="number"
            step="any"
            inputMode="decimal"
            placeholder=""
            icon={null}
            classname=""
            iconPositionRight
            label="Precio de venta (imp. excl.)"
            description="Precio con imp. excluido"
            defaultValue={
              data?.price != null && Number.isFinite(price) ? String(price) : ""
            }
            onWritting={(value) => syncPrice(value)}
          />
        </div>

        <div className="col-md-4 col-sm-12">
          <Input
            id="pvimpinclInput"
            type="number"
            step="any"
            inputMode="decimal"
            placeholder=""
            icon={null}
            classname=""
            iconPositionRight
            label="Precio de venta (imp. incl.)"
            required
            error={error}
            description="Al editarlo se recalcula el precio sin impuestos"
            defaultValue={priceWithTax}
            onWritting={(value) => syncPrice(value, true)}
          />
        </div>

        <div className="col-md-4 col-sm-12">
          <Input
            id="pvcomparative"
            type="number"
            step="any"
            inputMode="decimal"
            placeholder=""
            icon={null}
            classname=""
            iconPositionRight
            label="Precio de comparación"
            description="Precio de comparación con el anterior"
            defaultValue={
              data?.comparisonPrice != null
                ? Number(data.comparisonPrice).toFixed(2)
                : ""
            }
          />
        </div>
      </div>
    </>
  );
};

const Detail = ({
  referenceLayout,
  savedVersion,
  available,
  onAvailableChange,
  onNameChange,
  fieldErrors,
  data,
  hasLote,
  handleSwitchChange,
  handleData,
  saving,
  removedImages = [],
  onRemoveImage = () => null,
  onDraftChange = () => {},
}) => {
  const dispatch = useDispatch();
  const [previews, setPreviews] = useState({});
  const [imageError, setImageError] = useState("");
  useEffect(() => {
    setPreviews({});
    for (let position = 0; position < 5; position++) {
      const input = getElement(`img-alt-${position}`);
      if (input) input.value = "";
    }
  }, [savedVersion]);
  const getImageByPosition = (position) => {
    if (removedImages.includes(position)) return null;
    const result = data?.images?.find((url) => {
      if (typeof url !== "string") {
        return false;
      }

      return url.split("/").pop()?.startsWith(`${position}-`);
    });

    return result || null;
  };

  const handleImage = (event, position) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (
      !["image/png", "image/jpeg", "image/webp", "image/gif"].includes(
        file.type,
      ) ||
      file.size > 10 * 1024 * 1024
    ) {
      event.target.value = "";
      setImageError(
        "Selecciona una imagen PNG, JPG, WebP o GIF de hasta 10 MB.",
      );
      return;
    }
    setImageError("");
    const label = event.target.closest("label");

    if (label) {
      label.style.color = "white";
    }

    const reader = new FileReader();

    reader.onload = (readerEvent) => {
      setPreviews((previous) => ({
        ...previous,
        [position]: readerEvent.target.result,
      }));
    };

    reader.readAsDataURL(file);
  };

  const requestFormData = async () => {
    const name = getElementValue("nameInput").trim();

    if (name.length <= 4) {
      return;
    }

    const fields = [
      {
        id: "nameTPVInput",
        request: `Nombre destinado al TPV del producto ${name}.`,
      },
      {
        id: "descriptionProduct",
        request: `Descripción que defina el producto ${name}.`,
      },
      {
        id: "seoNameInput",
        request: `Título para los metadatos orientado al SEO que se usará en la página del producto ${name}.`,
      },
      {
        id: "seoUrlInput",
        request: `Transforma el nombre ${name} con guiones para usarlo en una URL.`,
      },
      {
        id: "seoDescriptionInput",
        request: `Descripción para los metadatos orientada al SEO que se usará en la página del producto ${name}.`,
      },
    ];

    const emptyFields = fields
      .filter(({ id }) => {
        const element = getElement(id);

        return element && String(element.value ?? "").trim() === "";
      })
      .map((field) => ({
        ...field,
        value: "",
      }));

    if (emptyFields.length === 0) {
      return;
    }

    const promptDetailed = `
Completa el siguiente array de objetos completando solo la propiedad "value" con texto plano, basándote exclusivamente en el campo "request".
Devuélveme únicamente el array actualizado, sin ningún texto adicional, ni comentarios ni funciones.

${JSON.stringify(emptyFields, null, 2)}
`;

    try {
      const response = await callChatGTP(promptDetailed);

      let completedFields;

      try {
        completedFields =
          typeof response === "string" ? JSON.parse(response) : response;
      } catch (parseError) {
        console.error("Error procesando la respuesta de IA:", parseError);

        dispatch(
          setAlert({
            called: true,
            title: "Error",
            message: "Ha ocurrido un error, inténtalo de nuevo más tarde.",
            type: "error",
          }),
        );

        return;
      }

      if (!Array.isArray(completedFields)) {
        throw new Error("La respuesta de IA no tiene el formato esperado.");
      }

      completedFields.forEach(({ id, value }) => {
        const element = getElement(id);

        if (element) {
          element.value = value ?? "";
        }
      });

      onDraftChange();
    } catch (error) {
      console.error("Error completando los campos mediante IA:", error);

      dispatch(
        setAlert({
          called: true,
          title: "Error",
          message: "Ha ocurrido un error, inténtalo de nuevo más tarde.",
          type: "error",
        }),
      );
    }
  };

  return (
    <div className="productDetailsGrid">
      <div className="productDetailFields">
        <Input
          id="nameInput"
          label="Nombre del producto"
          required
          error={fieldErrors.name}
          onWritting={onNameChange}
          defaultValue={data?.name || ""}
          placeholder="Nombre del producto"
        />
        <Input
          id="nameTPVInput"
          label="Nombre TPV"
          description="Si es distinto, es el que se ve en el TPV"
          defaultValue={data?.shortName || ""}
          placeholder="Nombre en el TPV"
        />
        <Input
          id="referenceInput"
          label="Referencia"
          description="Se usa en el TPV y en los tickets"
          defaultValue={data?.reference || ""}
        />
        <Input
          id="barcodeInput"
          label="Código de barras"
          description="EAN del envase o del lote"
          defaultValue={data?.barcode || ""}
        />
        <label
          aria-label="Usar número de lote"
          htmlFor="loteSwitch"
          className="productBatchToggleRow"
        >
          <span>
            <strong>Usar número de lote</strong>
            <small>Actívalo para controlar el lote de este producto</small>
          </span>
          <input
            id="loteSwitch"
            type="checkbox"
            role="switch"
            checked={hasLote}
            onChange={handleSwitchChange}
          />
        </label>
        {hasLote ? (
          <Input
            id="loteInput"
            label="Nº de lote"
            ariaLabel="Número de lote"
            defaultValue={data?.batchNumber || ""}
          />
        ) : (
          <div className="productBatchDisabled">
            <span>Nº de lote</span>
            <p>Activa el control de lote para rellenarlo</p>
          </div>
        )}
        <div className="productDescriptionField">
          <Textarea
            id="descriptionProduct"
            label="Descripción del producto"
            rows={3}
            defaultValue={data?.description || ""}
            placeholder="Ingredientes, sabor o lo que hace especial este producto"
          />
        </div>
        {!referenceLayout && (
          <Input
            id="order"
            label="Orden en la tienda"
            type="number"
            defaultValue={data?.order ?? ""}
            description="Opcional. Posición del producto en la carta."
          />
        )}
      </div>
      {referenceLayout && (
        <label
          aria-label="Disponible en tienda y TPV"
          htmlFor="productAvailabilitySwitch"
          className="productAvailability"
        >
          <span>
            <strong>Disponible en tienda y TPV</strong>
            <small>Desactivarlo oculta el producto sin eliminarlo.</small>
          </span>
          <input
            type="checkbox"
            role="switch"
            id="productAvailabilitySwitch"
            checked={available}
            disabled={saving}
            onChange={(e) => onAvailableChange(e.target.checked)}
          />
        </label>
      )}
      <section className="productImagesSection">
        <h3>Imágenes</h3>
        <p>La imagen destacada es la que se ve en la tienda y en el TPV.</p>
        {imageError && <p role="alert">{imageError}</p>}
        <div className="referenceGallery">
          {[0, 1, 2, 3, 4].map((position) => {
            const image = previews[position] || getImageByPosition(position);
            const featured =
              [0, 1, 2, 3, 4].find(
                (p) => previews[p] || getImageByPosition(p),
              ) === position;
            return (
              <div
                className={`referenceImageTile ${featured ? "is-featured" : ""}`}
                key={position}
                hidden={!image}
              >
                <label
                  aria-label={`Cambiar imagen ${position + 1}`}
                  htmlFor={`img-alt-${position}`}
                  style={{
                    backgroundImage: image ? `url('${image}')` : "none",
                  }}
                >
                  {featured && (
                    <span className="referenceImageBadge">Destacada</span>
                  )}
                  <input
                    type="file"
                    id={`img-alt-${position}`}
                    aria-label={
                      position === 0
                        ? "Subir imagen destacada"
                        : `Subir imagen alternativa ${position}`
                    }
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    disabled={saving}
                    onChange={(event) => handleImage(event, position)}
                  />
                </label>
                {image && (
                  <button
                    type="button"
                    aria-label={
                      featured
                        ? "Eliminar imagen destacada"
                        : `Eliminar imagen alternativa ${position}`
                    }
                    disabled={saving}
                    onClick={() => {
                      setPreviews((previous) => ({
                        ...previous,
                        [position]: null,
                      }));
                      const input = getElement(`img-alt-${position}`);
                      if (input) input.value = "";
                      if (getImageByPosition(position)) onRemoveImage(position);
                    }}
                  >
                    <LuTrash2 aria-hidden="true" />
                  </button>
                )}
              </div>
            );
          })}
          {[0, 1, 2, 3, 4].some(
            (position) => !previews[position] && !getImageByPosition(position),
          ) && (
            <button
              type="button"
              className="productAddImage"
              disabled={saving}
              onClick={() => {
                const position = [0, 1, 2, 3, 4].find(
                  (p) => !previews[p] && !getImageByPosition(p),
                );
                getElement(`img-alt-${position}`)?.click();
              }}
            >
              <span>+</span>Añadir
            </button>
          )}
        </div>
        {removedImages.length > 0 && (
          <p className="referenceHelp">
            {removedImages.length} imagen(es) pendiente(s) de eliminar al
            guardar.{" "}
            <button
              type="button"
              disabled={saving}
              onClick={() => removedImages.forEach(onRemoveImage)}
            >
              Deshacer
            </button>
          </p>
        )}
      </section>
      <details className="referenceAdvanced">
        <summary>Más opciones</summary>
        {referenceLayout && (
          <Input
            id="order"
            label="Orden en la tienda"
            type="number"
            defaultValue={data?.order ?? ""}
            description="Posición del producto en la carta."
          />
        )}
        <div className="productInlineActions">
          <button
            type="button"
            className="customButton"
            onClick={requestFormData}
            disabled={saving}
          >
            {IconIA}
            <span>Proponer textos con IA</span>
          </button>
          {!referenceLayout && (
            <button
              type="button"
              className="customButton customButtonColor2"
              onClick={handleData}
              disabled={saving}
            >
              {saving ? "Guardando…" : "Guardar"}
            </button>
          )}
        </div>
      </details>
    </div>
  );
};
